import { Button, ColumnTable, useMobile } from '@octano/global-ui';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ClassTypeLegend from '../../../components/ClassTypeLegend/ClassTypeLegend';
import useCurrentTimeSubscription from '../../../hooks/useCurrentTimeSubscription';
import useServerSettings from '../../../hooks/useServerSettings';
import useTeacherClassManagementSetting from '../../../hooks/useTeacherClassManagementSetting';
import { useUserState } from '../../../hooks/useUserState';
import { ActivityName } from '../../../types/Class';
import showModalsectionClosedAt from '../../../utils/showModalSectionClosedAt';
import { SelectedClass } from '../parts/PastClassesSearch';
interface Props {
  onClassRegistration: (classData: SelectedClass) => void;
  onClassRecovery: (classData: SelectedClass) => void;
  onClassChangeClassroom: (classData: SelectedClass) => void;
}
function useColumn(props: Props): ColumnTable<SelectedClass>[] {
  const {
    onClassRegistration,
    onClassRecovery,
    onClassChangeClassroom,
  } = props;
  const { t } = useTranslation();
  const isMobile = useMobile();
  const { data: teacherClassMgmt } = useTeacherClassManagementSetting();

  const { offset } = useServerSettings();
  const currentTime = useCurrentTimeSubscription({
    offset,
    refreshEvery: 15000,
  });

  const { userData } = useUserState();

  const canRegisterOrRecover = useCallback(
    (schedule: string, date: string) => {
      const endTimeStr = schedule.split(' - ')[1];
      const [hours, minutes] = endTimeStr.split(':').map((n) => Number(n));

      const classEndTime = dayjs(date)
        .hour(hours)
        .minute(minutes)
        .second(0)
        .utcOffset(offset, true);

      return currentTime.isAfter(classEndTime);
    },
    [currentTime, offset],
  );

  return useMemo(() => {
    if (isMobile) {
      return [
        {
          headerText: t(`common.terms.date`),
          columnName: 'date',
        },
        {
          headerText: t(`common.terms.modules`),
          columnName: 'modules',
        },
        {
          headerText: '',
          columnName: 'action',
          width: '180px',
          cellFormat: (data) => {
            const { schedule, date } = data.row;
            const disableBtn = !canRegisterOrRecover(schedule, date);
            const buttons: JSX.Element[] = [];

            if (teacherClassMgmt?.updateClassroomAllowed) {
              buttons.push(
                <Button
                  key="modify"
                  text={t(`common.btnModifyClassroom`)}
                  outlined
                  onClick={() => onClassChangeClassroom(data.row)}
                  size="sm"
                  className="mr-2"
                  disabled={disableBtn}
                />,
              );
            }
            if (teacherClassMgmt?.rescheduleClassAllowed) {
              buttons.push(
                <Button
                  key="reschedule"
                  text={t(`common.btnReschedule`)}
                  outlined
                  onClick={() => {
                    if (data.row.sectionClosedAt) {
                      showModalsectionClosedAt(t);
                    } else {
                      onClassRecovery(data.row);
                    }
                  }}
                  size="sm"
                  className="mr-2"
                  disabled={disableBtn}
                />,
              );
            }
            buttons.push(
              <Button
                text={t(`common.btnRegisterschedule`)}
                outlined
                onClick={() => {
                  if (data.row.sectionClosedAt) {
                    showModalsectionClosedAt(t);
                  } else {
                    onClassRegistration(data.row);
                  }
                }}
                size="sm"
                disabled={disableBtn}
              />,
            );

            return <div className="d-flex justify-content-end">{buttons}</div>;
          },
        },
      ];
    } else {
      return [
        {
          headerText: t(`common.terms.date`),
          columnName: 'date',
        },
        {
          headerText: t(`common.terms.modules`),
          columnName: 'modules',
        },
        {
          headerText: t(`common.terms.schedule`),
          columnName: 'schedule',
        },
        {
          headerText: t(`common.terms.activity`),
          columnName: 'activity',
          cellFormat: (data) => (
            <ClassTypeLegend activityName={data.row.activity as ActivityName} />
          ),
        },
        {
          headerText: t(`common.terms.responsability`),
          columnName: '123',
          cellFormat: (data) => {
            const isHeadTeacher = data.row.professors.some(
              (p: any) => p.account.teacherId === userData?.teacherId,
            );
            return isHeadTeacher
              ? t(`common.terms.headTeacher`)
              : t(`common.terms.classResponsible`);
          },
        },
        {
          headerText: '',
          columnName: 'action',
          cellFormat: (data) => {
            const { schedule, date } = data.row;
            const disableBtn = !canRegisterOrRecover(schedule, date);

            const isHeadTeacher = data.row.professors.some(
              (p: any) => p.account.teacherId === userData?.teacherId,
            );
            const buttons: JSX.Element[] = [];

            if (teacherClassMgmt?.updateClassroomAllowed) {
              buttons.push(
                <Button
                  key="modify"
                  text={t(`common.btnModifyClassroom`)}
                  outlined
                  onClick={() => onClassChangeClassroom(data.row)}
                  size="sm"
                  className="mr-2"
                  disabled={disableBtn}
                />,
              );
            }
            if (teacherClassMgmt?.rescheduleClassAllowed) {
              buttons.push(
                <Button
                  key="reschedule"
                  text={t(`common.btnReschedule`)}
                  outlined
                  onClick={() => {
                    if (data.row.sectionClosedAt) {
                      showModalsectionClosedAt(t);
                    } else {
                      onClassRecovery(data.row);
                    }
                  }}
                  size="sm"
                  className="mr-2"
                  disabled={disableBtn || !isHeadTeacher}
                />,
              );
            }
            buttons.push(
              <Button
                text={t(`common.btnRegisterschedule`)}
                outlined
                onClick={() => {
                  if (data.row.sectionClosedAt) {
                    showModalsectionClosedAt(t);
                  } else {
                    onClassRegistration(data.row);
                  }
                }}
                size="sm"
                disabled={disableBtn}
              />,
            );
            return <div className="d-flex justify-content-end">{buttons}</div>;
          },
        },
      ];
    }
  }, [
    isMobile,
    t,
    userData,
    canRegisterOrRecover,
    onClassRecovery,
    onClassRegistration,
    onClassChangeClassroom,
    teacherClassMgmt,
  ]);
}

export default useColumn;
